/** Internal dependencies */
import { loadingSpinner, loadmoreSpinner } from './spinners';
import { updateItems } from './updateItems';
import { deleteItems, finish, prepareUrl } from './utilities';
import { fetchHandler } from '../utilities/fetchHandler';
import { fadeIn, fadeOut } from '../utilities';
import { getDataTaxQueries } from './getFilters/getDataTaxQueries';
import { getQueryOptions } from './getFilters/getQueryOptions';

/**
 * This function handles the actual call to our API endpoint.
 *
 * @author Kosh <kosh@cubetech.ch>
 * @author rafhun <rafhun@cubetech.ch>
 *
 * @version x.x.x
 * @since 3.0.0
 * @since x.x.x No actual api call if server side rendered
 *
 * @export
 * @param {HTMLElement} parent The `[data-ct-rest]` parent element
 * @param {array} currentFilters Array of query strings of currently active filters and query arguments
 * @param {boolean} reset Denotes whether contents should be replaced or added to.
 * @param {boolean} [loadmoreClick=false]
 */
export function apiCall(parent, currentFilters, reset, loadmoreClick = false) {
    // Get API information
    const { ctRest, ctRestInstance } = parent.dataset;
    const api = JSON.parse(ctRest);
    // Get the prepared API endpoint url.
    const restUrl = prepareUrl(api.endpoint, api);
    // Get relevant DOM elements within `parent`
    const error = parent.querySelector(`[data-ct-error="${ctRestInstance}"]`);
    const notFound = parent.querySelector(`[data-ct-notfound="${ctRestInstance}"]`);
    const btnLoadMore = parent.querySelector(`[data-ct-loadmore="${ctRestInstance}"]`);
    const noMoreEntries = parent.querySelector(`.no-more-entries[data-ct-rest-instance="${ctRestInstance}"]`);
    const itemsContainer = parent.querySelector(`[data-ct-items="${ctRestInstance}"]`);
    // Get nonce from body
    const nonce = document.body.dataset.ctRestNonce;

    // Hide no more entries notice
    noMoreEntries && fadeOut(noMoreEntries);

    // Put together the options we need for the `fetch` call
    const fetchOptions = {};
    if (nonce && nonce.length) {
        const headers = new Headers();
        headers.append('X-WP-Nonce', nonce);
        fetchOptions.headers = headers;
    }

    if (parent.dataset.ctRestSsr !== 'true') {
        // Activate appropriate loading spinner
        if (reset) {
            loadingSpinner(parent);
        } else {
            loadmoreSpinner(parent);
        }
    }

    // render to string and replace commas with custom | separator
    const apiFilter = currentFilters.join('&').replaceAll('%2C', encodeURIComponent('|'));
    const dataFilters = [];
    const dataTaxQueries = getDataTaxQueries(parent);
    if (dataTaxQueries) {
        dataFilters.push(dataTaxQueries);
    }
    const queryOptions = getQueryOptions(api);
    if (queryOptions) {
        dataFilters.push(queryOptions);
    }
    const dataFilter = dataFilters.join('&').replaceAll('%2C', encodeURIComponent('|'));
    let filters = apiFilter;
    if (apiFilter !== '' && dataFilter !== '') {
        filters = `${filters}&`;
    }

    if (dataFilter !== '') {
        filters = `${filters}${dataFilter}`;
    }

    // Actual call to the API endpoint
    if (parent.dataset.ctRestSsr !== 'true') {
        fetchHandler({ url: `${restUrl}?${filters}`, ...fetchOptions })
            .then((result) => {
                // Update URL to reflect selected filters
                let replaceUrl = location.href.split('?')[0];
                if (apiFilter !== '') {
                    replaceUrl += `?${apiFilter}`;
                }

                history.replaceState(null, '', replaceUrl);

                // Remove existing items if this is a reset call
                if (reset) {
                    deleteItems(itemsContainer);
                }

                // Flag to check whether results data is an array
                const isArray = Array.isArray(result.data);

                // Initially make sure the `error` and `notFound` elements are hidden
                error?.classList.add('d-none');
                notFound?.classList.add('d-none');

                // There was a result, but no additional data found
                if (result.data && result.data === null) {
                    // If this was a load more click, show the `noMoreEntries` message
                    if (loadmoreClick === true) {
                        noMoreEntries && fadeIn(noMoreEntries);
                    } else {
                        // Else show the nothing found message
                        notFound?.classList.remove('d-none');
                    }

                    // Hide the load more button
                    btnLoadMore?.classList.add('d-none');

                    const noItemsFoundEvent = new CustomEvent('ct.noItemsFound', {
                        bubbles: true,
                        detail: { result },
                    });

                    parent.dispatchEvent(noItemsFoundEvent);
                }

                // There are results, let's handle them
                if (isArray) {
                    // Update the items displayed
                    updateItems(parent, result);

                    // none found message if the data array is empty
                    if (result.data && result.data.length <= 0) {
                        notFound?.classList.remove('d-none');
                    }

                    // Decide whether the load more button needs to be visible or hidden based on query information and settings
                    if (result.data && parseInt(result.offset) + result.data.length >= result.total) {
                        btnLoadMore?.classList.add('d-none');
                    } else {
                        btnLoadMore?.classList.remove('d-none');
                    }

                    const itemsUpdatedEvent = new CustomEvent('ct.itemsUpdated', {
                        detail: { result },
                    });

                    parent.dispatchEvent(itemsUpdatedEvent);
                } else {
                    // offset 0 means we are on page 1, no results found needs to be displayed
                    if (result.offset === '0') {
                        notFound?.classList.remove('d-none');
                        finish(parent); // hides the loadmore button
                    }

                    // we are on a higher page, meaning there are results. We only need to hide the load more button
                    btnLoadMore?.classList.add('d-none');

                    const noItemsFoundEvent = new CustomEvent('ct.noItemsFound', {
                        bubbles: true,
                        detail: { result },
                    });

                    parent.dispatchEvent(noItemsFoundEvent);

                    return;
                }

                // Hide load more button if we are on the last page
                if (result.currentpage >= result.totalpages) {
                    finish(parent);
                } else {
                    // In all other cases we want to show the load more button
                    btnLoadMore && fadeIn(btnLoadMore);
                }
            })
            .catch((errorMsg) => {
                // There has been an error while running fetch
                error.classList.remove('d-none');
                console.error('There has been a problem with your fetch operation:', errorMsg);
            })
            .finally(() => {
                // `.finally()` is always run once all promises are resolved or have failed

                // Hide the appropriate spinner
                if (reset) {
                    loadingSpinner(parent, false);
                } else {
                    loadmoreSpinner(parent, false);
                }
            });
    } else {
        parent.removeAttribute('data-ct-rest-ssr');
        loadingSpinner(parent, false);
    }
}
